import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAdminId as resolveAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import {
  AlertTriangle,
  Trash2,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Package,
  Link2Off,
} from 'lucide-react';

interface MappingInfo {
  map_id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  product_quantity: number;
  is_auto_generated: boolean;
  last_pushed_qty: number | null;
  last_pushed_at: string | null;
}

interface ConflictItem {
  shopify_inventory_item_id: number;
  shopify_product_id: number;
  shopify_variant_id: number;
  mapping_count: number;
  mappings: MappingInfo[];
}

interface MappingConflictsProps {
  onResolved?: () => void;
}

export const MappingConflicts: React.FC<MappingConflictsProps> = ({ onResolved }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState<ConflictItem[]>([]);
  const [expandedConflict, setExpandedConflict] = useState<number | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const fetchConflicts = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_mapping_conflicts', {
        p_admin_id: adminId,
      });

      if (fetchError) throw fetchError;
      setConflicts(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conflicts');
    } finally {
      setLoading(false);
    }
  }, [getAdminId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const adminId = getAdminId();
      if (!adminId) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase.rpc('get_mapping_conflicts', {
          p_admin_id: adminId,
        });

        if (cancelled) return;
        if (fetchError) throw fetchError;
        setConflicts(data || []);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch conflicts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [getAdminId]);

  const handleResolve = async (
    keepMapId: string,
    shopifyInventoryItemId: number
  ) => {
    const adminId = getAdminId();
    if (!adminId) return;

    setResolving(keepMapId);
    setError(null);
    setSuccess(null);

    try {
      const { data, error: resolveError } = await supabase.rpc('resolve_mapping_conflict', {
        p_admin_id: adminId,
        p_keep_map_id: keepMapId,
        p_shopify_inventory_item_id: shopifyInventoryItemId,
      });

      if (resolveError) throw resolveError;

      const result = data as { deleted_count: number };
      setSuccess(`Resolved conflict: removed ${result.deleted_count} duplicate mapping(s)`);
      await fetchConflicts();
      onResolved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleCleanupAll = async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    setCleaningUp(true);
    setError(null);
    setSuccess(null);

    try {
      const { data, error: cleanupError } = await supabase.rpc('cleanup_duplicate_mappings', {
        p_admin_id: adminId,
      });

      if (cleanupError) throw cleanupError;

      const result = data as { deleted_mappings: number; remaining_conflicts: number };
      setSuccess(`Cleanup complete: removed ${result.deleted_mappings} duplicate mapping(s)`);
      await fetchConflicts();
      onResolved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup conflicts');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (conflicts.length === 0) {
    return null;
  }

  return (
    <div className="card p-6 border-2 border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Mapping Conflicts Detected
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {conflicts.length} Shopify product(s) are linked to multiple local products
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={fetchConflicts}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            size="sm"
            onClick={handleCleanupAll}
            loading={cleaningUp}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Auto-Fix All
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
          {success}
        </div>
      )}

      <div className="space-y-3">
        {conflicts.map((conflict) => (
          <div
            key={conflict.shopify_inventory_item_id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
          >
            <button
              onClick={() =>
                setExpandedConflict(
                  expandedConflict === conflict.shopify_inventory_item_id
                    ? null
                    : conflict.shopify_inventory_item_id
                )
              }
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-gray-400" />
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Shopify Variant #{conflict.shopify_variant_id}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {conflict.mapping_count} local products linked
                  </p>
                </div>
              </div>
              {expandedConflict === conflict.shopify_inventory_item_id ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {expandedConflict === conflict.shopify_inventory_item_id && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Select which local product should be linked to this Shopify variant.
                  The others will be unlinked.
                </p>

                {conflict.mappings.map((mapping) => (
                  <div
                    key={mapping.map_id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      mapping.is_auto_generated
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                        : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {mapping.product_name}
                        </span>
                        {!mapping.is_auto_generated && (
                          <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                            Real SKU
                          </span>
                        )}
                        {mapping.is_auto_generated && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                            Auto-generated
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span>SKU: {mapping.product_sku || 'N/A'}</span>
                        <span>Qty: {mapping.product_quantity}</span>
                        {mapping.last_pushed_qty !== null && (
                          <span>Last pushed: {mapping.last_pushed_qty}</span>
                        )}
                      </div>
                    </div>

                    <Button
                      size="sm"
                      onClick={() =>
                        handleResolve(mapping.map_id, conflict.shopify_inventory_item_id)
                      }
                      loading={resolving === mapping.map_id}
                      disabled={resolving !== null}
                      className={
                        mapping.is_auto_generated
                          ? 'bg-gray-600 hover:bg-gray-700'
                          : 'bg-green-600 hover:bg-green-700'
                      }
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Keep This
                    </Button>
                  </div>
                ))}

                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <Link2Off className="h-3 w-3 inline mr-1" />
                    Products with "Real SKU" are recommended as they have proper inventory tracking.
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
